import { Alert, Button, Grid } from "@mui/material";
import React, { useMemo, useState } from "react";
import PageLayout from "../components/common/page-layout";
import EvalSetBuilder from "../components/doc-extraction-evals/eval-set-builder";
import RunLauncher from "../components/doc-extraction-evals/run-launcher";
import RunsTable from "../components/doc-extraction-evals/runs-table";
import { RequireAuth } from "../hooks/use-auth";
import { useDocExtractionEvalSetsQuery } from "../generated/graphql";

/**
 * The document-extraction eval-runner dashboard: build or reuse a frozen eval
 * set, launch a single-arm run against it, and watch runs settle. Distinct from
 * the legacy doc-scanning schema tool at `/docs`.
 */
const DocExtractionEvalsPage = () => {
  // Override the app-wide `errorPolicy: "ignore"` so a failed load (e.g. the DE
  // proxy 503 when it is not configured) surfaces as an error we can render and
  // retry, instead of silently collapsing to an empty "no sets" picker.
  const { data, error, refetch } = useDocExtractionEvalSetsQuery({
    variables: { limit: 100 },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const sets = useMemo(() => data?.docExtractionEvalSets ?? [], [data]);

  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [runsRefreshKey, setRunsRefreshKey] = useState(0);

  const selectedSet = sets.find((s) => s.id === selectedSetId) ?? null;

  return (
    <RequireAuth>
      <PageLayout
        subtitle="New document-extraction pipeline"
        title="Doc extraction evals"
      >
        {error !== undefined && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Failed to load eval sets: {error.message}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EvalSetBuilder
              sets={sets}
              selectedSetId={selectedSetId}
              onSelectSet={setSelectedSetId}
              onSetCreated={async (id) => {
                await refetch();
                setSelectedSetId(id);
              }}
            />
          </Grid>
          {selectedSet !== null && (
            <Grid item xs={12}>
              <RunLauncher
                set={selectedSet}
                onLaunched={() => {
                  setRunsRefreshKey((k) => k + 1);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <RunsTable evalSetId={selectedSetId} refreshKey={runsRefreshKey} />
          </Grid>
        </Grid>
      </PageLayout>
    </RequireAuth>
  );
};

export default DocExtractionEvalsPage;
